// src/components/enduser/profile/ProfilePage.tsx

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useEnduserAuth } from "../../../contexts/EnduserAuthContext";
import { enduserAuthService } from "../../../services/enduser/enduserAuthService";
import MotionCard from "../../shared/MotionCard";
import { subscriptionService } from "../../../services/enduser/subscriptionService";

interface UserInfo {
  id: number;
  name: string;
  email: string;
  phone_number: string;
  user_type: "personal" | "commercial";
  company_name?: string;
  verified: boolean;
  free_access: boolean;
  last_login: string;
}

interface SubscriptionInfo {
  subscription_status:
    | "none"
    | "trial"
    | "active"
    | "expired"
    | "cancelled"
    | "free_access";
  can_cancel_within_48h: boolean;
  has_active_subscription_no_refund?: boolean;
  trial_ends_at?: string;
  subscription_ends_at?: string;
  can_calculate: boolean;
  free_access?: boolean;
  subscription_details?: {
    plan_name: string;
    plan_name_hebrew: string;
    amount_paid: number;
    payment_date: string;
  };
}

const ProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const { verifySession } = useEnduserAuth();

  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>("");

  // Delete account states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string>("");
  // Subscription states
  const [subscriptionInfo, setSubscriptionInfo] =
    useState<SubscriptionInfo | null>(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [cancelError, setCancelError] = useState<string>("");

  useEffect(() => {
    loadUserProfile();
    loadSubscriptionInfo();
  }, []);
  const loadUserProfile = async () => {
    setIsLoading(true);
    setError("");

    try {
      const response = await enduserAuthService.getProfile();

      if (response.res === "OK" && response.data) {
        setUserInfo(response.data);
      } else {
        setError(response.msg || "שגיאה בטעינת פרטי המשתמש");
      }
    } catch (error) {
      console.error("Error loading profile:", error);
      setError("שגיאה בטעינת פרטי המשתמש");
    } finally {
      setIsLoading(false);
    }
  };

  const loadSubscriptionInfo = async () => {
    setSubscriptionLoading(true);
    try {
      const response = await subscriptionService.getSubscriptionStatus();
      if (response.res === "OK" && response.data) {
        setSubscriptionInfo(response.data);
      }
    } catch (error) {
      console.error("Error loading subscription:", error);
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    setIsCancelling(true);
    setCancelError("");

    try {
      const response = await subscriptionService.cancelSubscription();
      if (response.res === "OK") {
        setShowCancelModal(false);

        // רענון ה-Context - חשוב לחכות!
        await verifySession();

        // המתנה קצרה לעדכון ה-state ואז העברה עם הודעה
        setTimeout(() => {
          alert("המנוי בוטל בהצלחה. ההחזר יועבר תוך מספר ימי עסקים.");
          navigate("/enduser/dashboard");
        }, 100);
      } else {
        setCancelError(response.msg || "שגיאה בביטול המנוי");
      }
    } catch (error) {
      console.error("Error cancelling subscription:", error);
      setCancelError("שגיאה בביטול המנוי");
    } finally {
      setIsCancelling(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== "DELETE") {
      setDeleteError("יש להקליד DELETE לאישור המחיקה");
      return;
    }

    setIsDeleting(true);
    setDeleteError("");

    try {
      const response =
        await enduserAuthService.deleteAccount(deleteConfirmText);

      if (response.res === "OK") {
        // Success - user is already logged out by the service
        alert("החשבון נמחק בהצלחה. תועבר לדף הבית.");
        navigate("/");
      } else {
        setDeleteError(response.msg || "שגיאה במחיקת החשבון");
      }
    } catch (error) {
      console.error("Error deleting account:", error);
      setDeleteError("שגיאה במחיקת החשבון. אנא נסה שוב מאוחר יותר");
    } finally {
      setIsDeleting(false);
    }
  };

  if (isLoading) {
    return (
      <div
        className="min-h-screen bg-gray-50 flex items-center justify-center"
        dir="rtl"
      >
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">טוען פרטי משתמש...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="min-h-screen bg-gray-50 flex items-center justify-center p-4"
        dir="rtl"
      >
        <MotionCard className="max-w-md w-full">
          <div className="text-center">
            <div className="text-5xl mb-4">❌</div>
            <h2 className="text-xl font-bold text-red-600 mb-2">שגיאה</h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={loadUserProfile}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
            >
              נסה שוב
            </button>
          </div>
        </MotionCard>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8"
      dir="rtl"
    >
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">אזור אישי</h1>
          <p className="mt-2 text-gray-600">ניהול פרטי החשבון והגדרות</p>
        </div>

        {/* User Info Card */}
        <MotionCard className="mb-6">
          <div className="p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
              <span className="text-2xl ml-2">👤</span>
              פרטי חשבון
            </h2>

            <div className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    שם מלא
                  </label>
                  <p className="text-gray-900 font-medium">
                    {userInfo?.name || "לא זמין"}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    דואר אלקטרוני
                  </label>
                  <p className="text-gray-900 font-medium">
                    {userInfo?.email || "לא זמין"}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    טלפון
                  </label>
                  <p className="text-gray-900 font-medium">
                    {userInfo?.phone_number || "לא זמין"}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    סוג חשבון
                  </label>
                  <p className="text-gray-900 font-medium">
                    {userInfo?.user_type === "personal" ? "אישי" : "עסקי"}
                  </p>
                </div>

                {userInfo?.user_type === "commercial" &&
                  userInfo?.company_name && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        שם חברה
                      </label>
                      <p className="text-gray-900 font-medium">
                        {userInfo.company_name}
                      </p>
                    </div>
                  )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    סטטוס אימות
                  </label>
                  <p
                    className={`font-medium ${
                      userInfo?.verified ? "text-green-700" : "text-yellow-700"
                    }`}
                  >
                    {userInfo?.verified ? "✅ מאומת" : "⏳ ממתין לאימות"}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </MotionCard>

        {/* Subscription Info - Full Section */}
        <MotionCard className="mb-6">
          <div className="p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
              <span className="text-2xl ml-2">💳</span>
              מנוי ותשלומים
            </h2>

            {subscriptionLoading ? (
              <div className="flex items-center justify-center py-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="mr-3 text-gray-600">טוען מידע על מנוי...</span>
              </div>
            ) : subscriptionInfo ? (
              <div className="space-y-4">
                {/* סטטוס מנוי */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <p className="text-sm text-gray-600">סטטוס מנוי</p>
                    <p
                      className={`font-bold text-lg ${
                        subscriptionInfo.subscription_status === "active"
                          ? "text-green-600"
                          : subscriptionInfo.subscription_status === "trial"
                            ? "text-blue-600"
                            : "text-red-600"
                      }`}
                    >
                      {subscriptionInfo.subscription_status === "active" && (
                        <>
                          ✅ מנוי פעיל
                          {userInfo?.free_access
                            ? " - גישה חופשית"
                            : subscriptionInfo.subscription_details
                                  ?.plan_name === "daily_plan"
                              ? " - יומי"
                              : subscriptionInfo.subscription_details
                                    ?.plan_name === "monthly_plan"
                                ? " - חודשי"
                                : subscriptionInfo.subscription_details
                                      ?.plan_name === "quarterly_plan"
                                  ? " - רבעוני"
                                  : subscriptionInfo.subscription_details
                                        ?.plan_name === "yearly_plan"
                                    ? " - שנתי"
                                    : ""}
                        </>
                      )}
                      {subscriptionInfo.subscription_status === "trial" &&
                        "⏳ תקופת ניסיון"}
                      {subscriptionInfo.subscription_status === "expired" &&
                        "❌ פג תוקף"}
                      {subscriptionInfo.subscription_status === "cancelled" &&
                        "🚫 בוטל"}
                      {subscriptionInfo.subscription_status === "none" &&
                        "📋 אין מנוי"}
                    </p>
                  </div>

                  {/* Badge לגישה חופשית */}
                  {userInfo?.free_access && (
                    <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                      🎉 גישה חופשית
                    </span>
                  )}
                </div>

                {/* פרטי מנוי - רק אם יש מנוי פעיל */}
                {subscriptionInfo.subscription_details && (
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="p-3 bg-blue-50 rounded-lg">
                      <p className="text-sm text-gray-600">שם התוכנית</p>
                      <p className="font-medium text-gray-900">
                        {subscriptionInfo.subscription_details.plan_name}
                      </p>
                    </div>
                    <div className="p-3 bg-blue-50 rounded-lg">
                      <p className="text-sm text-gray-600">סכום ששולם</p>
                      <p className="font-medium text-gray-900">
                        ₪
                        {subscriptionInfo.subscription_details.amount_paid?.toLocaleString(
                          "he-IL",
                        )}
                      </p>
                    </div>
                  </div>
                )}

                {/* תאריך תפוגה */}
                {(subscriptionInfo.subscription_ends_at ||
                  subscriptionInfo.trial_ends_at) && (
                  <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-sm text-yellow-800">
                      <span className="font-medium">📅 תאריך תפוגה: </span>
                      {new Date(
                        subscriptionInfo.subscription_ends_at ||
                          subscriptionInfo.trial_ends_at!,
                      ).toLocaleDateString("he-IL", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })}
                    </p>
                  </div>
                )}

                {/* כפתורי פעולה */}
                <div className="flex flex-wrap gap-3 pt-2">
                  {/* כפתור ביטול - רק אם אפשר לבטל תוך 48 שעות */}
                  {subscriptionInfo.can_cancel_within_48h && (
                    <button
                      onClick={() => setShowCancelModal(true)}
                      className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors text-sm font-medium"
                    >
                      ביטול מנוי
                    </button>
                  )}

                  {/* כפתור שדרוג/רכישה */}
                  {(subscriptionInfo.subscription_status === "none" ||
                    subscriptionInfo.subscription_status === "expired") && (
                    <button
                      onClick={() => navigate("/enduser/subscription")}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                    >
                      רכישת מנוי
                    </button>
                  )}

                  {/* כפתור חידוש - למנוי שעומד לפוג */}
                  {subscriptionInfo.subscription_status === "active" && (
                    <button
                      onClick={() => navigate("/enduser/subscription")}
                      className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm font-medium"
                    >
                      צפייה בתוכניות נוספות
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <p className="text-gray-600">לא נמצא מידע על מנוי</p>
            )}
          </div>
        </MotionCard>

        {/* Cancel Subscription Modal */}
        {showCancelModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div
              className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
              dir="rtl"
            >
              <h3 className="text-xl font-bold text-red-600 mb-4 flex items-center">
                <span className="text-2xl ml-2">⚠️</span>
                ביטול מנוי
              </h3>

              <div className="mb-6">
                <p className="text-gray-900 mb-3">
                  האם אתה בטוח שברצונך לבטל את המנוי?
                </p>
                <div
                  className={`border rounded-lg p-3 mb-3 ${
                    subscriptionInfo?.subscription_details?.plan_name ===
                    "monthly_plan"
                      ? "bg-green-50 border-green-200"
                      : "bg-blue-50 border-blue-200"
                  }`}
                >
                  <p
                    className={`text-sm ${
                      subscriptionInfo?.subscription_details?.plan_name ===
                      "monthly_plan"
                        ? "text-green-800"
                        : "text-blue-800"
                    }`}
                  >
                    {"📞 מנהל המערכת יצור איתך קשר בנוגע להחזר כספי יחסי."}
                  </p>
                </div>
                <p className="text-gray-600 text-sm">
                  לאחר הביטול תאבד גישה למחשבונים עד לרכישת מנוי חדש.
                </p>

                {cancelError && (
                  <p className="text-red-600 text-sm mt-3 p-2 bg-red-50 rounded">
                    {cancelError}
                  </p>
                )}
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowCancelModal(false);
                    setCancelError("");
                  }}
                  className="flex-1 bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors"
                  disabled={isCancelling}
                >
                  חזור
                </button>
                <button
                  onClick={handleCancelSubscription}
                  disabled={isCancelling}
                  className="flex-1 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  {isCancelling ? (
                    <span className="flex items-center justify-center">
                      <span className="animate-spin mr-2">⏳</span>
                      מבטל...
                    </span>
                  ) : (
                    "אשר ביטול"
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Danger Zone - Delete Account */}
        <MotionCard className="border-2 border-red-200">
          <div className="p-6">
            <h2 className="text-xl font-bold text-red-600 mb-4 flex items-center">
              <span className="text-2xl ml-2">⚠️</span>
              אזור מסוכן
            </h2>

            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <h3 className="font-bold text-red-900 mb-2">🗑️ מחיקת חשבון</h3>
              <p className="text-red-800 text-sm mb-2">מחיקת החשבון תגרום ל:</p>
              <ul className="text-red-800 text-sm space-y-1 mr-4">
                <li>• מחיקת כל הנתונים האישיים שלך</li>
                <li>• מחיקת כל החישובים השמורים</li>
                <li>• ניתוק מיידי מהמערכת</li>
                <li>
                  • <strong>פעולה בלתי הפיכה!</strong>
                </li>
              </ul>
            </div>

            <button
              onClick={() => setShowDeleteModal(true)}
              className="bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700 transition-colors"
            >
              מחק את החשבון שלי
            </button>
          </div>
        </MotionCard>

        {/* Delete Confirmation Modal */}
        {showDeleteModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div
              className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
              dir="rtl"
            >
              <h3 className="text-2xl font-bold text-red-600 mb-4 flex items-center">
                <span className="text-3xl ml-2">⚠️</span>
                אזהרה: מחיקת חשבון
              </h3>

              <div className="mb-6">
                {/* Check if user needs to cancel subscription first */}
                {subscriptionInfo?.subscription_status === "active" &&
                  subscriptionInfo?.can_cancel_within_48h && (
                    <div className="bg-orange-50 border border-orange-300 rounded-lg p-4 mb-4">
                      <p className="text-orange-800 font-medium mb-2">
                        ⚠️ לא ניתן למחוק את החשבון כרגע
                      </p>
                      <p className="text-orange-700 text-sm mb-3">
                        יש לך מנוי פעיל עם זכאות להחזר כספי. יש לבטל את המנוי
                        תחילה.
                      </p>
                      <button
                        onClick={() => {
                          setShowDeleteModal(false);
                          setShowCancelModal(true);
                        }}
                        className="bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 transition-colors text-sm font-medium"
                      >
                        עבור לביטול מנוי
                      </button>
                    </div>
                  )}

                {/* Show warning for monthly subscription without refund */}
                {subscriptionInfo?.has_active_subscription_no_refund && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                    <p className="text-blue-800 font-medium mb-2">ℹ️ שים לב</p>
                    <p className="text-blue-700 text-sm">
                      המנוי היומי/החודשי שלך יבוטל אוטומטית עם מחיקת החשבון. לא
                      יינתן החזר כספי.
                    </p>
                  </div>
                )}

                {/* Regular deletion flow - only show if allowed to delete */}
                {!(
                  subscriptionInfo?.subscription_status === "active" &&
                  subscriptionInfo?.can_cancel_within_48h
                ) && (
                  <>
                    <p className="text-gray-900 font-medium mb-3">
                      האם אתה בטוח לחלוטין?
                    </p>
                    <p className="text-gray-600 text-sm mb-3">
                      פעולה זו תמחק לצמיתות:
                    </p>
                    <ul className="text-gray-600 text-sm space-y-1 mr-4 mb-4">
                      <li>✓ את כל הפרטים האישיים שלך</li>
                      <li>✓ את כל החישובים השמורים</li>
                      <li>✓ את כל ההיסטוריה שלך במערכת</li>
                    </ul>

                    <div className="bg-yellow-50 border border-yellow-200 rounded p-3 mb-4">
                      <p className="text-yellow-900 text-sm font-medium">
                        💡 לאישור, הקלד:{" "}
                        <code className="bg-yellow-100 px-2 py-1 rounded">
                          DELETE
                        </code>
                      </p>
                    </div>

                    <input
                      type="text"
                      value={deleteConfirmText}
                      onChange={(e) => {
                        setDeleteConfirmText(e.target.value);
                        setDeleteError("");
                      }}
                      placeholder="הקלד DELETE"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                      disabled={isDeleting}
                    />

                    {deleteError && (
                      <p className="text-red-600 text-sm mt-2">{deleteError}</p>
                    )}
                  </>
                )}
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setDeleteConfirmText("");
                    setDeleteError("");
                  }}
                  className="flex-1 bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors"
                  disabled={isDeleting}
                >
                  {subscriptionInfo?.subscription_status === "active" &&
                  subscriptionInfo?.can_cancel_within_48h
                    ? "סגור"
                    : "ביטול"}
                </button>
                {!(
                  subscriptionInfo?.subscription_status === "active" &&
                  subscriptionInfo?.can_cancel_within_48h
                ) && (
                  <button
                    onClick={handleDeleteAccount}
                    disabled={isDeleting || deleteConfirmText !== "DELETE"}
                    className="flex-1 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isDeleting ? (
                      <span className="flex items-center justify-center">
                        <span className="animate-spin mr-2">⏳</span>
                        מוחק...
                      </span>
                    ) : (
                      "מחק את החשבון"
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfilePage;
